import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Table, Space, Modal, Form, Input, message, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axios/axios-instance';
import { Link } from 'react-router-dom';

interface AppTranslationKey {
  id: number;
  ui_key: string;
  value: string;
}

interface TranslationDetail {
  ui_id: number;
  ui_key: string;
  default_value: string;
  language_id: number;
  language_code: string;
  translation_id: number | null;
  translation_value: string;
}

interface PaginationData {
  current_page: number;
  from: number;
  to: number;
  last_page: number;
  per_page: number;
  total_page: number;
  total: number;
}

const AppTranslation: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingDetails, setEditingDetails] = useState<TranslationDetail[]>([]);
  const [form] = Form.useForm();
  const [keys, setKeys] = useState<AppTranslationKey[]>([]);
  const [editForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [filteredKeys, setFilteredKeys] = useState<AppTranslationKey[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 0,
    from: 1,
    to: 1,
    last_page: 1,
    per_page: 20,
    total_page: 1,
    total: 0
  });

  const fetchKeys = async (page: number = 0, search: string = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page.toString());
      
      let endpoint = 'admin/setting/app/multi-lang/keys';
      if (search) {
        endpoint = 'admin/setting/app/multi-lang/search';
        params.append('q', search);
      }
      
      const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);
      const { data, ...paginationData } = response.data;
      setKeys(data);
      setFilteredKeys(data);
      setPagination(paginationData);
    } catch (error) {
      message.error(t('multi_lang.fetch_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleTableChange = (pagination: any) => {
    fetchKeys(pagination.current - 1, searchText); // API uses 0-based indexing
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    fetchKeys(0, value); // Reset to first page when searching
  };

  const handleDelete = async (record: AppTranslationKey) => {
    Modal.confirm({
      title: t('multi_lang.delete_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('multi_lang.delete_content', { key: record.ui_key }),
      okText: t('multi_lang.yes'),
      okType: 'danger',
      cancelText: t('multi_lang.no'),
      onOk: async () => {
        try {
          await axiosInstance.delete(`admin/setting/app/multi-lang/${record.id}`);
          message.success(t('multi_lang.deleted', { key: record.ui_key }));
          fetchKeys(); // Refresh the list
        } catch (error) {
          message.error(t('multi_lang.delete_error'));
        }
      },
    });
  };

  const handleTranslationSave = async () => {
    try {
      setLoading(true);
      const values = await editForm.validateFields();
      
      // Get the ui_id from the first item since it's same for all
      const ui_id = editingDetails[0]?.ui_id;
      
      const requests = Object.keys(values)
        .filter(key => key.startsWith('translation_'))
        .map(key => {
          const [, languageId] = key.split('_');
          return {
            app_ui_id: ui_id,
            language_id: parseInt(languageId),
            value: values[key]
          };
        });

      await axiosInstance.post(`admin/setting/app/multi-lang/${ui_id}`, {
        ui_key: values.ui_key,
        value: values.default_value,
        requests
      });

      message.success(t('multi_lang.updated'));
      setIsEditModalVisible(false);
      fetchKeys(); // Refresh the main list
    } catch (error) {
      message.error(t('multi_lang.update_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (record: AppTranslationKey) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`admin/setting/app/multi-lang/${record.id}`);
      const { key, translations } = response.data.data;
      setEditingDetails(translations);
      
      // Set initial form values
      const initialValues: { [key: string]: string } = {
        ui_key: key.ui_key,
        default_value: key.value
      };
      
      // Add translation values
      translations.forEach((detail: TranslationDetail) => {
        initialValues[`translation_${detail.language_id}`] = detail.translation_value;
      });
      
      editForm.setFieldsValue(initialValues);
      setIsEditModalVisible(true);
    } catch (error) {
      message.error(t('multi_lang.detail_error'));
    } finally {
      setLoading(false);
    }
  };

  const detailColumns = [
    {
      title: t('multi_lang.col.language'),
      dataIndex: 'language_code',
      key: 'language_code',
      width: '30%',
    },
    {
      title: t('multi_lang.col.translation'),
      dataIndex: 'translation_value',
      key: 'translation_value',
      width: '70%',
      render: (_: string, record: TranslationDetail) => (
        <Form.Item
          name={`translation_${record.language_id}`}
          style={{ margin: 0 }}
        >
          <Input.TextArea
            rows={2}
            placeholder={t('multi_lang.enter_translation', { lang: record.language_code })}
          />
        </Form.Item>
      ),
    },
  ];

  const columns = [
    {
      title: t('multi_lang.col.id'),
      dataIndex: 'id',
      key: 'id',
      width: '10%',
      align: 'center' as const
    },
    {
      title: t('multi_lang.col.ui_key'),
      dataIndex: 'ui_key',
      key: 'ui_key',
      width: '25%',
    },
    {
      title: t('multi_lang.col.value'),
      dataIndex: 'value',
      key: 'value',
      width: '45%',
      render: (value: string) => (
        <div style={{ 
          maxHeight: '3em', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: '1.5em'
        }}>
          {value}
        </div>
      )
    },
    {
      title: t('multi_lang.col.actions'),
      key: 'actions',
      width: '20%',
      align: 'center' as const,
      render: (_: any, record: AppTranslationKey) => (
        <Space split={<Divider type="vertical" />}>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('multi_lang.edit')}
          </Button>
          <Button 
            type="link" 
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            {t('multi_lang.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const handleAddKey = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleSubmit = async (values: { uiKey: string; value: string }) => {
    try {
      setLoading(true);
      const payload = {
        ui_key: values.uiKey,
        value: values.value
      };
      
      const response = await axiosInstance.post('admin/setting/app/multi-lang/keys', payload);
      setIsModalVisible(false);
      message.success(t('multi_lang.created', { key: values.uiKey }));
      
      // Fetch the new list first
      await fetchKeys();
      
      // Then open edit modal for the new key
      handleEdit({ 
        id: response.data.data.id, 
        ui_key: response.data.data.ui_key, 
        value: response.data.data.value 
      });
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || t('multi_lang.add_error');
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title={t('multi_lang.app_title')}>
      <div style={{ 
        marginBottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddKey}
        >
          {t('multi_lang.add_key')}
        </Button>
        <Input.Search
          placeholder={t('multi_lang.search_ph')}
          allowClear
          style={{ width: 300 }}
          onSearch={handleSearch}
          onChange={(e) => setSearchText(e.target.value)}
          value={searchText}
          enterButton
        />
      </div>

      <Table<AppTranslationKey>
        loading={loading}
        columns={columns}
        dataSource={filteredKeys}
        rowKey="id"
        pagination={{
          current: pagination.current_page + 1, // Convert 0-based to 1-based
          pageSize: pagination.per_page,
          total: pagination.total,
          showSizeChanger: false, // Hide page size changer
          showQuickJumper: false, // Hide quick jumper
          showTotal: (total, range) => t('multi_lang.range_total', { from: range[0], to: range[1], total }),
        }}
        onChange={handleTableChange}
      />

      <Modal
        title={t('multi_lang.edit_title')}
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={() => setIsEditModalVisible(false)}>
            {t('btn.cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={handleTranslationSave}
          >
            {t('multi_lang.save')}
          </Button>,
        ]}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <div style={{ 
            marginBottom: 16, 
            padding: '16px',
            background: 'var(--app-bg)',
            borderRadius: '6px',
            border: '1px solid var(--app-border-light)'
          }}>
            <Form.Item
              name="ui_key"
              label={t('multi_lang.col.ui_key')}
              style={{ marginBottom: 16 }}
            >
              <Input placeholder={t('multi_lang.enter_ui_key')} />
            </Form.Item>
            <Form.Item
              name="default_value"
              label={t('multi_lang.col.default_value')}
              style={{ margin: 0 }}
            >
              <Input.TextArea 
                rows={3}
                placeholder={t('multi_lang.enter_default')}
              />
            </Form.Item>
          </div>
          <Table
            columns={detailColumns}
            dataSource={editingDetails}
            rowKey="language_id"
            pagination={false}
          />
        </Form>
      </Modal>

      <Modal
        title={t('multi_lang.add_title')}
        open={isModalVisible}
        onOk={form.submit}
        onCancel={() => setIsModalVisible(false)}
        maskClosable={false}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="uiKey"
            label={t('multi_lang.col.ui_key')}
            extra={t('multi_lang.ui_key_help')}
            rules={[{ required: true, message: t('multi_lang.ui_key_required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="value"
            label={t('multi_lang.col.value')}
            extra={t('multi_lang.value_help')}
            rules={[{ required: true, message: t('multi_lang.value_required') }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default AppTranslation; 